import { createSlice, createSelector } from '@reduxjs/toolkit';
import { createAppAsyncThunk } from '../createAppAsyncThunk';
import { API_POST } from '../../services';
import { KpiDashBoardModel, Card, KpiGrid } from '../../models/operationsmodels/KpiModels';
import { RADRequest } from '../../models/common/RADRequest';
import type { RootState } from '../store';
import { env } from '../../env';
import { ENDPOINTS } from '../../constants/endPoints';
import { GRID_ORDER_BY_CARD } from '../../constants/constants';

interface OperationsState {
  kpiDashboard: KpiDashBoardModel | null;
  isCardLoading: boolean;
  isGridLoading: boolean;
}

const initialState: OperationsState = {
  kpiDashboard: null,
  isCardLoading: false,
  isGridLoading: false,
};

export const fetchOperationsKpiDetails = createAppAsyncThunk(
  'operations/fetchOperationsKpiDetails',
  async (params: RADRequest) => {
    const response = await API_POST(
      `${env.RAD_APP_API_URL}${ENDPOINTS.GET_OPERATIONS_KPI_DETAILS}`,
      params,
    );
    return response as KpiDashBoardModel;
  },
);

const operationsSlice = createSlice({
  name: 'operations',
  initialState,
  reducers: {
    // Reset dashboard data if needed
    resetKpiDashboard: state => {
      state.kpiDashboard = null;
    },
  },
  extraReducers: builder => {
    builder
      // fetchOperationsKpiDetails cases - now fetches both cards and grids
      .addCase(fetchOperationsKpiDetails.pending, state => {
        state.isCardLoading = true;
        state.isGridLoading = true;
        state.kpiDashboard = null;
      })
      .addCase(fetchOperationsKpiDetails.fulfilled, (state, action) => {
        state.kpiDashboard = action.payload;
        state.isCardLoading = false;
        state.isGridLoading = false;
      })
      .addCase(fetchOperationsKpiDetails.rejected, state => {
        state.isCardLoading = false;
        state.isGridLoading = false;
        state.kpiDashboard = null;
      });
  },
});

export const { resetKpiDashboard } = operationsSlice.actions;

// Stable empty array references to prevent unnecessary rerenders
const EMPTY_CARDS_ARRAY: Card[] = [];
const EMPTY_GRIDS_ARRAY: KpiGrid[] = [];

// Selectors for KPI Cards
export const selectAllKpiCards = (state: RootState) =>
  state.operations.kpiDashboard?.cards ?? EMPTY_CARDS_ARRAY;

// Check if kpiDashboard is null (indicates API error)
export const selectKpiDashboardIsNull = (state: RootState) =>
  state.operations.kpiDashboard === null;

// Selectors for KPI Grids
export const selectGridLoading = (state: RootState) => state.operations.isGridLoading;
export const selectCardLoading = (state: RootState) => state.operations.isCardLoading;

// Filter grids by cardName (case-insensitive) and sort by predefined order
export const selectKpiGridsByCardName = createSelector(
  [
    (state: RootState) => state.operations.kpiDashboard?.kpiGrids ?? EMPTY_GRIDS_ARRAY,
    (_state: RootState, cardName: string) => cardName,
  ],
  (kpiGrids, cardName) => {
    const filteredGrids = kpiGrids.filter(
      grid => grid.cardName?.toUpperCase() === cardName.toUpperCase(),
    );

    // Get the ordering array for this card (if exists)
    const order = GRID_ORDER_BY_CARD[cardName.toUpperCase()];
    if (!order) {
      return filteredGrids; // Return unsorted if no order defined
    }

    // Sort grids based on the defined order
    return filteredGrids.sort((a, b) => {
      const indexA = order.findIndex(name => name.toUpperCase() === a.title?.toUpperCase());
      const indexB = order.findIndex(name => name.toUpperCase() === b.title?.toUpperCase());

      // If not found in order, push to end
      const posA = indexA === -1 ? order.length : indexA;
      const posB = indexB === -1 ? order.length : indexB;

      return posA - posB;
    });
  },
);

// Filter KPI details by list of KPI codes
export const selectKpisByKpiCodes = createSelector(
  [
    (state: RootState) => state.operations.kpiDashboard?.kpiGrids ?? EMPTY_GRIDS_ARRAY,
    (_state: RootState, kpiCodes: string[]) => kpiCodes,
  ],
  (kpiGrids, kpiCodes) => {
    const upperCodes = kpiCodes.map(code => code.toUpperCase());
    return kpiGrids
      .flatMap(grid => grid.kpiDetails)
      .filter(kpi => upperCodes.includes(kpi.kpiCode?.toUpperCase()));
  },
);

// Filter KPI details by grid name and card name (case-insensitive)
// Note: gridName is a property on KpiDetail, cardName is on KpiGrid
export const selectKpisByGridName = createSelector(
  [
    (state: RootState) => state.operations.kpiDashboard?.kpiGrids ?? EMPTY_GRIDS_ARRAY,
    (_state: RootState, gridName: string) => gridName,
    (_state: RootState, _gridName: string, cardName: string) => cardName,
  ],
  (kpiGrids, gridName, cardName) => {
    return kpiGrids
      .filter(grid => grid.cardName?.toUpperCase() === cardName.toUpperCase())
      .flatMap(grid => grid.kpiDetails)
      .filter(kpi => kpi.gridName?.toUpperCase() === gridName.toUpperCase());
  },
);

// Card name ordering for print view
const CARD_ORDER = ['INVENTORY', 'MARKETING', 'PEOPLE', 'B2B'];

/**
 * Selector for print view: Returns all KPI grids in display order
 * Order: INVENTORY → MARKETING → PEOPLE → B2B
 * Within each card, grids are sorted by GRID_ORDER_BY_CARD
 */
export const selectAllOperationsGridsInOrder = createSelector(
  [(state: RootState) => state.operations.kpiDashboard?.kpiGrids ?? EMPTY_GRIDS_ARRAY],
  kpiGrids => {
    // Group grids by card name
    const gridsByCard: Record<string, KpiGrid[]> = {};

    kpiGrids.forEach(grid => {
      const cardName = grid.cardName?.toUpperCase() || 'OTHER';
      if (!gridsByCard[cardName]) {
        gridsByCard[cardName] = [];
      }
      gridsByCard[cardName].push(grid);
    });

    // Sort grids within each card using GRID_ORDER_BY_CARD
    Object.keys(gridsByCard).forEach(cardName => {
      const order = GRID_ORDER_BY_CARD[cardName];
      if (order) {
        gridsByCard[cardName].sort((a, b) => {
          const indexA = order.findIndex(name => name.toUpperCase() === a.title?.toUpperCase());
          const indexB = order.findIndex(name => name.toUpperCase() === b.title?.toUpperCase());

          const posA = indexA === -1 ? order.length : indexA;
          const posB = indexB === -1 ? order.length : indexB;

          return posA - posB;
        });
      }
    });

    // Flatten grids in card order
    const orderedGrids: KpiGrid[] = [];
    CARD_ORDER.forEach(cardName => {
      if (gridsByCard[cardName]) {
        orderedGrids.push(...gridsByCard[cardName]);
      }
    });

    // Add any remaining grids from cards not in CARD_ORDER
    Object.keys(gridsByCard).forEach(cardName => {
      if (!CARD_ORDER.includes(cardName)) {
        orderedGrids.push(...gridsByCard[cardName]);
      }
    });

    return orderedGrids;
  },
);

export default operationsSlice.reducer;
